import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaSignOutAlt,
  FaUser,
  FaUsers
} from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from '../style'
import * as A from '../Ads/style'
import * as C from './style'

type CommissionStatus = 'pending' | 'paid'

type CommissionItem = {
  id: string
  campaignName: string
  partnerName: string
  adStatus: 'active' | 'inactive'
  contractValue?: number | null
  commissionRate?: number | null
  commissionAmount?: number | null
  commissionStatus: CommissionStatus
  commissionPaidAt?: string | null
  commercialUserId: string
  commercialName: string
  email?: string
}

type CommissionResponse = {
  items: CommissionItem[]
  summary: {
    total: number
    pending: number
    paid: number
  }
}

const money = (value?: number | null) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value ?? 0))

const AdminCommissions = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const isCommercialOnly = Boolean(user?.isCommercial && !user?.isAdmin)
  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-commissions'],
    queryFn: async () => {
      const response = await api.get<CommissionResponse>(
        '/admin/ads/commissions'
      )
      return response.data
    },
    staleTime: 15 * 1000
  })

  const statusMutation = useMutation({
    mutationFn: async ({
      id,
      status
    }: {
      id: string
      status: CommissionStatus
    }) => {
      await api.patch(`/admin/ads/${id}/commission-status`, { status })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-commissions'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-ads'] })
      ])
      toast.success('Status da comissão atualizado.', { theme: 'dark' })
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível atualizar a comissão.',
        { theme: 'dark' }
      )
    }
  })

  const roleLabel = user?.isMaster
    ? 'Master'
    : user?.isAdmin
      ? 'Admin'
      : 'Comercial'

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaDollarSign />
            {roleLabel}
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            {!isCommercialOnly && (
              <>
                <S.SidebarNavLink to="/admin">
                  <FaChartLine />
                  Dashboard
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/usuarios">
                  <FaUsers />
                  Usuários
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/localidades">
                  <FaMapMarkerAlt />
                  Localidades
                </S.SidebarNavLink>
                <S.SidebarNavLink to="/admin/roteiros">
                  <FaRoute />
                  Roteiros
                </S.SidebarNavLink>
              </>
            )}
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            {!isCommercialOnly && (
              <S.SidebarNavLink to="/admin/radar">
                <FaSatelliteDish />
                Radar
              </S.SidebarNavLink>
            )}
            <S.SidebarNavLink to="/admin/comissoes" $active>
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna</S.TopbarEyebrow>
            <S.TopbarTitle>Comissões</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt={fullName || roleLabel} />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || roleLabel}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>{roleLabel}</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>
                {isCommercialOnly ? 'Minhas comissões' : 'Comissões comerciais'}
              </h1>
              <p>
                {isCommercialOnly
                  ? 'Acompanhe as comissões geradas pelas suas parcerias.'
                  : 'Acompanhe e confirme o pagamento das comissões da equipe comercial.'}
              </p>
            </div>
          </S.DashboardHeader>

          {data && (
            <S.UserSummaryGrid>
              <S.UserSummaryCard>
                <FaDollarSign />
                <div>
                  <strong>{money(data.summary.total)}</strong>
                  <span>Total gerado</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="red">
                <FaDollarSign />
                <div>
                  <strong>{money(data.summary.pending)}</strong>
                  <span>Pendente</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="green">
                <FaDollarSign />
                <div>
                  <strong>{money(data.summary.paid)}</strong>
                  <span>Pago</span>
                </div>
              </S.UserSummaryCard>
            </S.UserSummaryGrid>
          )}

          {isLoading && <S.StateCard>Carregando comissões...</S.StateCard>}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar as comissões.
            </S.StateCard>
          )}

          {data && (
            <A.TableCard>
              <A.TableScroll>
                <A.AdTable>
                  <thead>
                    <tr>
                      <th>Parceria</th>
                      {!isCommercialOnly && <th>Comercial</th>}
                      <th>Contrato</th>
                      <th>%</th>
                      <th>Comissão</th>
                      <th>Status</th>
                      {!isCommercialOnly && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <C.Identity>
                            <strong>{item.partnerName}</strong>
                            <span>{item.campaignName}</span>
                          </C.Identity>
                        </td>
                        {!isCommercialOnly && (
                          <td>
                            <C.Identity>
                              <strong>{item.commercialName || '—'}</strong>
                              <span>{item.email || ''}</span>
                            </C.Identity>
                          </td>
                        )}
                        <td>{money(item.contractValue)}</td>
                        <td>
                          {item.commissionRate == null
                            ? '—'
                            : `${item.commissionRate}%`}
                        </td>
                        <td>
                          <C.Amount>{money(item.commissionAmount)}</C.Amount>
                        </td>
                        <td>
                          <A.StatusBadge
                            $active={item.commissionStatus === 'paid'}
                          >
                            {item.commissionStatus === 'paid'
                              ? 'Pago'
                              : 'Pendente'}
                          </A.StatusBadge>
                        </td>
                        {!isCommercialOnly && (
                          <td>
                            <A.Actions>
                              <button
                                type="button"
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  statusMutation.mutate({
                                    id: item.id,
                                    status:
                                      item.commissionStatus === 'paid'
                                        ? 'pending'
                                        : 'paid'
                                  })
                                }
                              >
                                {item.commissionStatus === 'paid'
                                  ? 'Reabrir'
                                  : 'Marcar como paga'}
                              </button>
                            </A.Actions>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </A.AdTable>
              </A.TableScroll>

              {!data.items.length && (
                <A.EmptyState>
                  Nenhuma comissão registrada até o momento.
                </A.EmptyState>
              )}
            </A.TableCard>
          )}
        </S.Content>
      </S.Main>
    </S.Page>
  )
}

export default AdminCommissions
